/**
 * Follow a keyset cursor to the end, and be honest when you did not get there.
 *
 * Separated from the HTTP call so the loop is unit-testable without mocking a fetch layer — the
 * admin suite is deliberately DOM-and-network-free, and a loop that can only be exercised through
 * `apiFetch` is a loop nothing tests.
 *
 * WHY IT REPORTS TRUNCATION RATHER THAN JUST CAPPING. This helper exists because three consumers
 * of the admins list needed COMPLETENESS and were reading one 50-row page while asserting in
 * comments that they had everything. A page cap that silently returned a partial list and
 * presented it as whole would be that same defect with a bigger number: still a partial answer
 * wearing a complete one's clothes, just harder to hit.
 *
 * So the cap is a SAFETY VALVE, not a limit the caller is expected to meet, and `truncated` is how
 * the caller finds out it fired. A caller that ignores it gets the old bug back — which is why the
 * owner picker surfaces it rather than swallowing it.
 *
 * The cap also bounds two server behaviours no client should trust its way out of: a cursor that
 * never terminates, and a cursor that repeats forever. Neither is expected; both would otherwise
 * hang the page.
 */

export interface Page<T> {
  items: T[];
  nextCursor?: string;
}

export interface CollectResult<T> {
  items: T[];
  /** True when the page cap stopped the walk before the server ran out of cursors. */
  truncated: boolean;
  /** How many requests were made, including the first. Useful for a log line or a test. */
  pages: number;
}

/**
 * Walk every page.
 *
 * `fetchPage(cursor)` is called with `undefined` for the first page and the previous page's cursor
 * thereafter. It is the caller's job to make that a request; this owns only the walking.
 */
export async function collectPages<T>(
  fetchPage: (cursor?: string) => Promise<Page<T>>,
  maxPages: number,
): Promise<CollectResult<T>> {
  // A cap below 1 would return nothing while reporting success, which is the failure this file is
  // about. Treat it as a programming error and fetch the first page regardless.
  const cap = Math.max(1, maxPages);

  const first = await fetchPage(undefined);
  const items = [...first.items];
  let cursor = first.nextCursor;
  let pages = 1;

  while (cursor && pages < cap) {
    const next = await fetchPage(cursor);
    items.push(...next.items);
    cursor = next.nextCursor;
    pages++;
  }

  // TRUNCATED means "the server still had more when we stopped", which is exactly `cursor` still
  // being set. It is deliberately NOT `pages === cap`: a walk that used its last permitted page and
  // happened to finish there is COMPLETE, and reporting it as partial would put a false warning in
  // front of an operator — the kind of alarm that teaches people to ignore alarms.
  return { items, truncated: Boolean(cursor), pages };
}
